/**
 * ContractSummaryStep Component
 * Step 5: Contract summary view with sub-tabs (read-only, available after submission)
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  PictureAsPdf as PdfIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Stop as TerminateIcon,
  Autorenew as RolloverIcon,
  Send as SubmitIcon,
} from '@mui/icons-material';
import { useContractFormContext } from '../ContractFormContext';
import { usePermissions } from '../../../../contexts/PermissionContext';
import { MODULES, PERMISSION_LEVELS } from '../../../../contexts/PermissionConstants';
import { apiPost } from '../../../../utils/apiClient';
import TabPanel, { a11yProps } from '../../../common/TabPanel';
import SummaryContractDetails from './summary/SummaryContractDetails';
import SummaryAmortizationTab from './summary/SummaryAmortizationTab';
import SummaryPaymentHistory from './summary/SummaryPaymentHistory';
import SummaryApprovalHistory from './summary/SummaryApprovalHistory';
import { DocumentUploadSection } from '../../../documents';
import { apiGet } from '../../../../utils/apiClient';
import useServerPDF from '../../../../hooks/useServerPDF';
import { format } from 'date-fns';
import ContractActionDialogs from './summary/ContractActionDialogs';

function ContractSummaryStep({ onEditContract }) {
  const {
    contractId,
    initialData,
    summaryData,
    setSummaryData,
    summaryLoading,
    setSummaryLoading,
  } = useContractFormContext();

  const [subTabValue, setSubTabValue] = useState(0);
  const { downloadPDF, isGenerating } = useServerPDF();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.WRITE);
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);

  // Action dialog states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false);
  const [rolloverDialogOpen, setRolloverDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Load summary data on mount.
  // No guard on summaryData: a contract can be rejected (and its schedule cleared
  // server-side by sp_contract_reject) between visits to this step, so we must
  // re-fetch every time to avoid showing or re-submitting a stale schedule.
  useEffect(() => {
    if (contractId) {
      loadSummaryData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- loadSummaryData is defined below
  }, [contractId]);

  const loadSummaryData = async () => {
    if (!contractId) return;

    setSummaryLoading(true);
    try {
      const [scheduleData, historyData, paymentsData, receiptsData, versionsData, adhocData, investorsData] = await Promise.all([
        apiGet(`/amortization/contract/${contractId}`).catch(() => ({ data: { schedule: [] } })),
        apiGet(`/contracts/${contractId}/approval-history`).catch(() => ({ data: [] })),
        apiGet(`/payments/contract/${contractId}`).catch(() => ({ data: [] })),
        apiGet(`/payments/receipts?contract_id=${contractId}`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${contractId}/schedule-versions`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${contractId}/adhoc-capital`).catch(() => ({ data: [] })),
        apiGet(`/contracts/${contractId}/investors`).catch(() => ({ data: [] })),
      ]);

      // Helper to extract data from various response formats
      const extractData = (response) => {
        if (!response) return [];
        if (response?.success && response?.data) return response.data;
        if (response?.data && Array.isArray(response.data)) return response.data;
        if (Array.isArray(response)) return response;
        return [];
      };

      // Extract schedule
      const extractSchedule = (response) => {
        if (!response) return [];
        if (response?.data?.schedule && Array.isArray(response.data.schedule)) {
          return response.data.schedule;
        }
        if (response?.schedule && Array.isArray(response.schedule)) {
          return response.schedule;
        }
        const data = extractData(response);
        return Array.isArray(data) ? data : [];
      };

      setSummaryData({
        schedule: extractSchedule(scheduleData),
        approvalHistory: extractData(historyData),
        payments: extractData(paymentsData),
        receipts: extractData(receiptsData),
        scheduleVersions: extractData(versionsData),
        adhocCapitalEvents: extractData(adhocData),
        investors: extractData(investorsData),
      });
    } catch (err) {
      console.error('Error loading summary data:', err);
      setSummaryData({
        schedule: [],
        approvalHistory: [],
        payments: [],
        receipts: [],
        scheduleVersions: [],
        adhocCapitalEvents: [],
        investors: [],
      });
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleSubTabChange = (event, newValue) => {
    setSubTabValue(newValue);
  };

  const handleDownloadContractPDF = async () => {
    if (!contractId) return;

    try {
      const filename = `Contract_${initialData?.reference || contractId}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'contract', id: contractId, filename });
    } catch (err) {
      console.error('Error downloading contract PDF:', err);
      alert('Failed to generate contract PDF: ' + (err.message || 'Please try again.'));
    }
  };

  const handleActivate = async () => {
    try {
      await apiPost(`/contracts/${contractId}/activate`);
      // Reload the page to get updated status
      window.location.reload();
    } catch (err) {
      console.error('Error activating contract:', err);
      alert('Failed to activate contract: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setSubmitting(true);
      await apiPost(`/contracts/${contractId}/submit-for-approval`);
      // Reload the page to get updated status
      window.location.reload();
    } catch (err) {
      console.error('Error submitting for approval:', err);
      alert('Failed to submit for approval: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  // Check if schedule exists for Draft contracts
  const hasSchedule = summaryData?.schedule?.length > 0;

  if (summaryLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading contract summary...</Typography>
      </Box>
    );
  }

  if (!initialData) {
    return (
      <Alert severity="warning">
        Contract data not available. Please go back and save the contract first.
      </Alert>
    );
  }

  return (
    <Box>
      {/* Sub-tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={subTabValue}
          onChange={handleSubTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Contract Details" {...a11yProps(0, 'summary')} />
          <Tab label="Amortization Schedule" {...a11yProps(1, 'summary')} />
          <Tab label="Documents" {...a11yProps(2, 'summary')} />
          <Tab label="Payment History" {...a11yProps(3, 'summary')} />
          <Tab label="Approval History" {...a11yProps(4, 'summary')} />
        </Tabs>
      </Paper>

      {/* Sub-tab content */}
      <TabPanel value={subTabValue} index={0} id="summary" padding={0}>
        <SummaryContractDetails
          contract={initialData}
          summaryData={summaryData}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={1} id="summary" padding={0}>
        <SummaryAmortizationTab
          contract={initialData}
          schedule={summaryData?.schedule || []}
          onRegenerate={loadSummaryData}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={2} id="summary" padding={0}>
        <DocumentUploadSection
          contractId={contractId}
          investorId={initialData?.investor_id}
          title="Contract Documents"
          collapsible={false}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={3} id="summary" padding={0}>
        <SummaryPaymentHistory
          contract={initialData}
          payments={summaryData?.payments || []}
          receipts={summaryData?.receipts || []}
          schedule={summaryData?.schedule || []}
        />
      </TabPanel>

      <TabPanel value={subTabValue} index={4} id="summary" padding={0}>
        <SummaryApprovalHistory
          approvalHistory={summaryData?.approvalHistory || []}
        />
      </TabPanel>

      {/* Action Dialogs */}
      <ContractActionDialogs
        contractId={contractId}
        contract={initialData}
        approveDialogOpen={approveDialogOpen}
        setApproveDialogOpen={setApproveDialogOpen}
        rejectDialogOpen={rejectDialogOpen}
        setRejectDialogOpen={setRejectDialogOpen}
        terminateDialogOpen={terminateDialogOpen}
        setTerminateDialogOpen={setTerminateDialogOpen}
        rolloverDialogOpen={rolloverDialogOpen}
        setRolloverDialogOpen={setRolloverDialogOpen}
        onActionComplete={loadSummaryData}
      />
    </Box>
  );
}

export default ContractSummaryStep;
